import { Spin } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { getFullVelocityUrl } from '../../../Helper';
import { SubPermissions, User } from '../../../model/AccountModel';
import { VDOItem, VisualObjectsTreeNode } from '../../../model/BadgeModel';
import { GraphicModeOption, RedlineConfigValues } from '../../../model/CommonModel';
import {
	BadgeStudio,
	BadgeStudioBarCode,
	BadgeStudioImage,
	BadgeStudioMagstripe,
	BadgeStudioSpaceObjects,
	BaseFontPositionProperties,
	C_ObjectType,
	C_PictureTypeCustom,
	Coordinate,
	E_ObjectTypes,
	GenericGraphicObject,
	GraphicActionType,
	GraphicPageMode,
	GraphicText,
	ShapeObject,
} from '../../../model/GraphicModel';
import { DesignIcon, InDesignIcon, InPreviewIcon, PreviewIcon } from '../../../resources/Icons';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import {
	setBadgeStudioBarCodeAction,
	setBadgeStudioImageAction,
	setBadgeStudioInfoAction,
	setBadgeStudioMagstripeAction,
	setCurrentRedlineConfigValuesAction,
	setDisableMenuOptsAction,
	setGraphicTextAction,
	setHasChangedAction,
	setViewModeAction,
	setVisualShapeObjectAction,
} from '../../../store/badge/actions';
import {
	badgeInfoSelector,
	currentRedlineConfigValuesSelector,
	drawingMenuOptsDisabledSelector,
	graphicActionTypeSelector,
	graphicDataSelector,
	spaceObjectsSelector,
	viewModeSelector,
} from '../../../store/badge/selectors';
import { SwitchSideMenu, VisualDataObjects } from '../../badge';
import { GraphicModeMenu, PropertiesWindow, canvasContainerId, canvasMainContainerId } from '../../common';
import { MenuBar } from '../../graphic';
import { MainMenu } from '../../graphic/MenuBar/MainMenu/MainMenu';
import { ToolsMenu } from './ToolsMenu/ToolsMenu';
import { ZoomMenu } from './ZoomMenu/ZoomMenu';
import styles from './badgeDesigner.module.scss';

const velocityUrl = getFullVelocityUrl();
const user: User = getUser();
const modeMenuOptions: GraphicModeOption[] = [
	{
		value: GraphicPageMode.Design,
		text: _('Design'),
		hasPermission: User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Edit).allowed,
		SelectedIcon: <InDesignIcon />,
		UnSelectedIcon: <DesignIcon />,
	},
	{
		value: GraphicPageMode.Live,
		text: _('Preview'),
		hasPermission: User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Preview).allowed,
		SelectedIcon: <InPreviewIcon />,
		UnSelectedIcon: <PreviewIcon />,
	},
];
const selectedObject: GraphicText = {
	ObjType: C_ObjectType.OT_TEXT,
	DataField: 'Service Number',
	FontInfo: {
		Id: 1,
		AutoSize: false,
		FontFamily: 'Tahoma',
		FontSize: 12,
		FontStyle: 1,
		TextColorHex: '#000000',
		DisplayText: 'sample Text',
		FontContainerStyle: 1,
	},
	Id: 1,
	Name: 'Test Text',
	PositionInfo: {
		Id: 1,
		Bottom: 0,
		Left: 0,
		Right: 0,
		Rotation: 0,
		Top: 0,
	},
	UDF: 14,
	AppearanceInfo: {
		Id: 1,
		Visible: true,
		LineWidth: 1,
		LineColor: '#000000',
		Transparency: 0,
		BackColor: '#FFFFFF',
		BackStyle: 1,
		FillColor: '#FFFFFF',
		FillStyle: 1,
		LineStyle: 1,
	},
};

const BadgeDesigner: React.FC = () => {
	const dispatch = useStoreDispatch();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [onLoadEndFinished, setOnLoadEndFinished] = useState<boolean>(false);

	const mode = useStoreSelector(viewModeSelector);
	const { Id, Name } = useStoreSelector(graphicDataSelector);
	const currentRedlineConfigValues = useStoreSelector<RedlineConfigValues>(currentRedlineConfigValuesSelector);
	const spaceObject = useStoreSelector<Partial<BadgeStudioSpaceObjects>>(spaceObjectsSelector);
	const disabledOpts: boolean = useStoreSelector<boolean>(drawingMenuOptsDisabledSelector);
	const graphicActionType: GraphicActionType = useStoreSelector<GraphicActionType>(graphicActionTypeSelector);

	const spaceObjectId = useRef('');
	const room = useRef(undefined);
	const textLayer = useRef(undefined);
	const graphicPageMode = useRef<GraphicPageMode>();

	const getGraphicPageMode = () => graphicPageMode.current;

	const setGraphicPageMode = value => (graphicPageMode.current = value);

	const badgeInfo = useStoreSelector<BadgeStudio>(badgeInfoSelector);
	const viewMode = useStoreSelector<GraphicPageMode>(viewModeSelector);
	//TODO: Replace add select logic
	// const selectedObject = useStoreSelector(selectGraphicObject(C_PictureTypeCustom.MDOT_TEXT, 1));
	const isDesignMode = useMemo(() => viewMode === GraphicPageMode.Design, [viewMode]);
	const renderPropsWindow = useMemo(() => isDesignMode /* && selectedObject */, [isDesignMode /* , selectedObject */]);
	const graphicType = useMemo(() => E_ObjectTypes.OTYP_MDOBJECT, []);

	useEffect(() => {
		if (mode !== getGraphicPageMode()) {
			setGraphicPageMode(mode);
		}
	}, [mode]);

	useEffect(() => {
		if (Name) {
			const { BadgeMagstripes, BarCodes, ShapeObjects, Images, Texts, FileName: MapFileName } = spaceObject;

			const fileName: string = `${velocityUrl}/Content/Graphics/${MapFileName}`;
		}
	}, [Name]);

	const getRedlineThickness = () => {
		const input = document.getElementById('cvjs_redlineThicknessInput') as HTMLInputElement;
		batch(() => {
			dispatch(
				setCurrentRedlineConfigValuesAction({
					...currentRedlineConfigValues,
					width: parseInt(input.value),
				})
			);
			setDisableMenuActions();
		});
	};

	const cvjs_ObjectSelected = (rMid: string) => {
		spaceObjectId.current = rMid;

		//setSelectedObject(rMid);
	};

	const cvjs_CanvasClick = (id, handle, entity) => {
		//setSelectedObject('');
	};

	const onLoadEnd = () => {
		setOnLoadEndFinished(true);
	};

	const setDisableMenuActions = () => {
		dispatch(setDisableMenuOptsAction(false));
	};

	const handleOnCanvasChanged = () => {
		dispatch(setHasChangedAction(true));
	};

	const handleOnChangeContainerCoordinates = (coordinate: Coordinate) => {};

	const handleOnDrop = (event: DragEvent) => {
		// prevent any default action
		event.preventDefault();
		const { currentTarget, clientX, clientY, dataTransfer } = event;
		const currentElement = currentTarget as HTMLElement;

		if (currentElement.id === canvasContainerId) {
			if (event.dataTransfer.types.includes('custom-type')) {
				const dataString = dataTransfer.getData('custom-type');
				const data = JSON.parse(dataString) as VisualObjectsTreeNode;

				switch (data.vdoItem) {
					case VDOItem.Text:
						const hiddenSpaceObject = getDummySpaceObject(data.vdoItem, clientX, clientY);

						const testRedline: Partial<BaseFontPositionProperties> = {
							TextColorHex: '#000000',
							DisplayText: '<Undefined Text>',
							FontSize: 16,
							Id: Math.floor(Math.random() * 100),
							FontFamily: 'Tahoma',
							Left: hiddenSpaceObject.translate_x,
							Bottom: hiddenSpaceObject.translate_y,
						};
						const newRedline = null;
						break;
					case VDOItem.Picture:
					case VDOItem.Signature:
						const spaceObjectId = getGuid();
						break;
					case VDOItem.BarCode:
						break;
					case VDOItem.MagStripe:
						break;
				}
			}
		}
	};

	const getDummySpaceObject = (vdoItem: VDOItem, drawingX: number, drawingY: number) => {
		const spaceObjectId = getGuid();

		const hiddenSpaceObject = null;

		return hiddenSpaceObject;
	};

	const handleOnChangeMode = (mode: number) => {
		dispatch(setViewModeAction(mode));
	};

	const updateItem = (obj: GenericGraphicObject) => {
		obj.Id = selectedObject.Id;
		switch (obj.ObjType) {
			case C_PictureTypeCustom.MDOT_DBBARCODE:
				dispatch(
					setBadgeStudioBarCodeAction({
						itemId: obj.Id,
						newItem: obj as BadgeStudioBarCode,
					})
				);
				break;
			case C_PictureTypeCustom.MDOT_TEXT:
			case C_PictureTypeCustom.MDOT_DBTEXT:
				dispatch(
					setGraphicTextAction({
						itemId: obj.Id,
						newItem: obj as GraphicText,
					})
				);
				break;
			case C_PictureTypeCustom.MDOT_DBMAGSTRIPE:
				dispatch(
					setBadgeStudioMagstripeAction({
						itemId: obj.Id,
						newItem: obj as BadgeStudioMagstripe,
					})
				);
				break;
			case C_PictureTypeCustom.MDOT_DBIMAGE:
			case C_ObjectType.OT_IMAGE:
			case C_ObjectType.OT_BITMAP:
				dispatch(
					setBadgeStudioImageAction({
						itemId: obj.Id,
						newItem: obj as BadgeStudioImage,
					})
				);
				break;
			case C_PictureTypeCustom.MDOT_VIO:
				dispatch(
					setVisualShapeObjectAction({
						itemId: obj.Id,
						newItem: obj as ShapeObject,
					})
				);
				break;
			default:
				dispatch(
					setBadgeStudioInfoAction({
						itemId: obj.Id,
						newItem: {
							...badgeInfo,
							Name: obj.Name,
						},
					})
				);
		}
	};

	return (
		<>
			<MenuBar>
				<MainMenu
					isMaps={false}
					containerId={canvasContainerId}
					mainContainerId={canvasMainContainerId}
					viewMode={mode}
					redlineConfiguration={currentRedlineConfigValues}
					graphicActionType={graphicActionType}
					disableOptions={disabledOpts}
				/>
				<GraphicModeMenu mode={mode} options={modeMenuOptions} disabled={disabledOpts} onChangeMode={handleOnChangeMode} />
			</MenuBar>
			<div id={canvasMainContainerId} className={styles.canvas}>
				<div className={styles.propertiesWindowContainer}>
					{isDesignMode && renderPropsWindow && (
						<PropertiesWindow currentObject={selectedObject} currentObjectType={graphicType} isBadge={true} updateItem={updateItem} />
					)}
					{isDesignMode && <VisualDataObjects />}
				</div>

				<ToolsMenu
					isMaps={false}
					containerId={canvasContainerId}
					mainContainerId={canvasMainContainerId}
					viewMode={mode}
					redlineConfiguration={currentRedlineConfigValues}
					graphicActionType={graphicActionType}
					disableOptions={disabledOpts}
				/>
				<ZoomMenu
					isMaps={false}
					containerId={canvasContainerId}
					mainContainerId={canvasMainContainerId}
					viewMode={mode}
					redlineConfiguration={currentRedlineConfigValues}
					graphicActionType={graphicActionType}
					disableOptions={disabledOpts}
				/>
				{false && /* isDoubleSided */ <SwitchSideMenu />}

				<Spin spinning={isLoading} tip={_('Loading')} wrapperClassName='canvasLoading' />
			</div>
		</>
	);
};

export { BadgeDesigner };
